/**
 * react-query hooks for the run approval gate, revenue leakage and credit
 * memos (server/src/routes/billingControls.ts). Keys live under ['billing', ...]
 * so a run start or a rate card edit refreshes them too.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/auth/AuthContext';

// ---------------------------------------------------------------------------
// Types (mirror src/approvals, src/credits, src/leakage)
// ---------------------------------------------------------------------------

export type ApprovalStatus = 'pending' | 'approved' | 'rejected' | 'created';

export interface PreviewLine {
  line_order: number;
  metric: string;
  item_id: string;
  item_name: string;
  description: string;
  qty: number;
  unit_price: number;
  amount: number;
  minimum_applied: boolean;
  /** QuickBooks TaxCodeRef sent on the line (absent on very old previews). */
  tax_code_ref?: string;
  /** Package C, only on a multi month invoice. */
  price_basis?: string;
  interval_scale?: number;
}

export interface ComparisonRow {
  item_id: string;
  label: string;
  previous_qty: number | null;
  qty: number | null;
  previous_amount: number | null;
  amount: number | null;
  delta_pct: number | null;
}

export interface ApprovalItem {
  id: string;
  merchant_key: string;
  merchant: string;
  invoice_group: number;
  invoice_period: string;
  usage_period: string;
  internal_ref: string;
  currency: string;
  status: ApprovalStatus;
  fingerprint: string;
  preview: {
    lines: PreviewLine[];
    subtotal: number;
    discount_pct: number | null;
    discount_amount: number;
    total: number;
    qbo_customer_id: string;
    customer_memo?: string;
    service_from?: string;
    service_to?: string;
    /** Absent on previews stored before 2026-10-15: show "not recorded", never 0. */
    tax_amount?: number;
    tax_code_ref?: string;
    /** Package C: present only when not TaxExcluded / not monthly. */
    global_tax_calculation?: 'TaxInclusive';
    billing_interval_months?: number;
    invoice_period_first?: string;
    invoice_period_last?: string;
    usage_periods?: string[];
    /** Group schedules: present only on a group that runs on its own schedule. */
    group_schedule?: { invoice_day: number; billing_timing: 'advance' | 'arrears'; billing_interval_months: number; invoice_anchor_month: number | null };
  };
  previous: { internal_ref: string; invoice_period: string; qbo_invoice_id: string | null; total: number | null } | null;
  exceptions: { severity: string; code: string; message: string; line_order?: number }[];
  first_seen_at: string;
  updated_at: string;
  approved_by: string | null;
  approved_at: string | null;
  rejected_by: string | null;
  rejected_at?: string | null;
  reject_reason: string | null;
  changes: { field: string; before: string | number | null; after: string | number | null }[] | null;
  created_invoice: { qbo_invoice_id: string; doc_number: string } | null;
  rebill_id: string | null;
  comparison: { rows: ComparisonRow[]; previous_total: number | null; total: number; total_delta_pct: number | null };
  periodClosed: boolean;
  /** Why the signed-in user cannot approve it right now; null when they can. */
  approveBlocker: string | null;
}

export interface ApprovalSettings {
  approvalRequired: boolean;
  /** Off by default: the scheduled run warns about a missed invoicing date instead of pricing it. */
  autoCatchUp: boolean;
  updated_by: string | null;
  updated_at: string | null;
  reason: string | null;
}

export interface ApprovalsResponse {
  items: ApprovalItem[];
  settings: ApprovalSettings;
  closedPeriods: string[];
}

export interface Rebill {
  id: string;
  merchant_key: string;
  invoice_group: number;
  invoice_period: string;
  usage_period: string;
  original_doc_number: string;
  internal_ref: string;
  credit_memo_id: string;
  status: 'requested' | 'awaiting_approval' | 'created' | 'failed';
  doc_number: string | null;
  requested_by: string;
  requested_at: string;
  error: string | null;
}

export interface LeakageFinding {
  key: string;
  rule: string;
  severity: 'error' | 'warning' | 'info';
  merchant_key: string | null;
  merchant: string;
  master_vendor_id: number | null;
  period: string | null;
  message: string;
  amount: number | null;
  currency: string | null;
  status: 'open' | 'acknowledged' | 'resolved';
  first_seen_at: string;
  last_seen_at: string;
  resolved_at: string | null;
  acknowledged_by: string | null;
  acknowledged_at: string | null;
  ack_note: string | null;
}

export interface LeakageResponse {
  findings: LeakageFinding[];
  counts: { open: number; acknowledged: number; resolved: number };
  runs: { run_id: string; at: string; usage_periods: string[]; open: number; new_findings: number; resolved: number }[];
}

export const REASON_CODES = ['pricing_error', 'usage_error', 'goodwill', 'duplicate', 'dispute_resolution', 'other'] as const;
export type ReasonCode = (typeof REASON_CODES)[number];
export const REASON_LABELS: Record<ReasonCode, string> = {
  pricing_error: 'Pricing error',
  usage_error: 'Usage error',
  goodwill: 'Goodwill',
  duplicate: 'Duplicate invoice',
  dispute_resolution: 'Dispute resolution',
  other: 'Other',
};

export interface CreditableInvoice {
  id: string;
  doc_number: string;
  customer_id: string;
  currency: string;
  total: number;
  balance: number | null;
  lines: { id: string; item_id: string; item_name: string; description: string; qty: number; unit_price: number; amount: number }[];
  discount_percent: number | null;
  net: number;
}

export interface CreditMemo {
  id: string;
  qbo_invoice_id: string;
  invoice_doc_number: string;
  qbo_customer_id: string;
  currency: string;
  mode: 'lines' | 'amount' | 'full';
  amount: number;
  reason_code: ReasonCode;
  note: string;
  status: 'pending' | 'created' | 'failed' | 'unknown';
  qbo_credit_memo_id: string | null;
  qbo_doc_number: string | null;
  error: string | null;
  created_by: string;
  created_at: string;
  rebill_id: string | null;
}

export type CreditContext =
  | { configured: false }
  | {
      configured: true;
      invoice: CreditableInvoice;
      credited: number;
      remaining: number;
      memos: CreditMemo[];
      rebill:
        | { possible: boolean; why: string | null; merchant_key: string; invoice_period: string; usage_period: string; invoice_group: number; periodClosed: boolean }
        | { possible: false; why: string };
    };

export type CreditInput =
  | { mode: 'full' | 'lines' | 'amount'; lineIds?: string[]; amount?: number; lineId?: string; reasonCode: ReasonCode; note: string; confirmDocNumber: string };

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

const seg = (s: string) => encodeURIComponent(s);
export const controlKeys = {
  approvals: (all: boolean) => ['billing', 'approvals', all] as const,
  leakage: (all: boolean) => ['billing', 'leakage', all] as const,
  credit: (invoiceId: string) => ['billing', 'credit', invoiceId] as const,
  rebills: ['billing', 'approvals', 'rebills'] as const,
};

export function useRebills() {
  const api = useApi();
  return useQuery({ queryKey: controlKeys.rebills, queryFn: () => api.get<{ rebills: Rebill[] }>('/api/billing/rebills') });
}

export function useStartRebill() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<{ executionId: string | null; warning: string | null }>(`/api/billing/rebills/${seg(id)}/start`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['billing'] }),
  });
}

export function useApprovals(all: boolean) {
  const api = useApi();
  return useQuery({ queryKey: controlKeys.approvals(all), queryFn: () => api.get<ApprovalsResponse>(`/api/billing/approvals${all ? '?status=all' : ''}`) });
}

export function useApprovalMutations() {
  const api = useApi();
  const qc = useQueryClient();
  const done = () => qc.invalidateQueries({ queryKey: ['billing', 'approvals'] });
  return {
    approve: useMutation({
      mutationFn: (v: { id: string; fingerprint: string }) => api.post(`/api/billing/approvals/${seg(v.id)}/approve`, { fingerprint: v.fingerprint }),
      onSuccess: done,
    }),
    reject: useMutation({ mutationFn: (v: { id: string; reason: string }) => api.post(`/api/billing/approvals/${seg(v.id)}/reject`, { reason: v.reason }), onSuccess: done }),
    approveAll: useMutation({
      mutationFn: (v: { confirmCount: number }) => api.post<{ approved: string[]; failed: { id: string; error: string }[] }>('/api/billing/approvals/approve-all', v),
      onSuccess: done,
    }),
    settings: useMutation({
      mutationFn: (v: Partial<Pick<ApprovalSettings, 'approvalRequired' | 'autoCatchUp'>> & { reason: string; confirm?: string }) =>
        api.put('/api/billing/approvals/settings', v),
      onSuccess: done,
    }),
    createNow: useMutation({
      mutationFn: (v: { period: string; confirmPeriod: string }) =>
        api.post<{ executionId: string | null }>('/api/billing/runs', { period: v.period, confirmPeriod: v.confirmPeriod, dryRun: false, approvedOnly: true }),
      onSuccess: () => qc.invalidateQueries({ queryKey: ['billing'] }),
    }),
  };
}

export function useLeakage(all: boolean) {
  const api = useApi();
  return useQuery({ queryKey: controlKeys.leakage(all), queryFn: () => api.get<LeakageResponse>(`/api/billing/leakage${all ? '?status=all' : ''}`) });
}

export function useAcknowledgeFinding() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { key: string; note: string }) => api.post('/api/billing/leakage/acknowledge', v),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['billing', 'leakage'] }),
  });
}

export function useCreditContext(invoiceId: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: controlKeys.credit(invoiceId ?? ''),
    queryFn: () => api.get<CreditContext>(`/api/billing/invoices/${seg(invoiceId!)}/credit-context`),
    enabled: invoiceId !== null,
    // Reads the invoice from QuickBooks: never refetch on focus.
    refetchOnWindowFocus: false,
    staleTime: 60_000,
  });
}

export function useCreditMutations(invoiceId: string) {
  const api = useApi();
  const qc = useQueryClient();
  const done = () => qc.invalidateQueries({ queryKey: ['billing'] });
  return {
    credit: useMutation({ mutationFn: (v: CreditInput) => api.post<{ creditMemo: CreditMemo }>(`/api/billing/invoices/${seg(invoiceId)}/credit-memos`, v), onSuccess: done }),
    rebill: useMutation({
      mutationFn: (v: { reasonCode: ReasonCode; note: string; confirmDocNumber: string; reopenReason?: string }) =>
        api.post<{ creditMemo: CreditMemo; rebill: { id: string }; warning: string | null }>(`/api/billing/invoices/${seg(invoiceId)}/correct-and-rebill`, v),
      onSuccess: done,
    }),
  };
}
